import { IPropertyDetailsResponse } from "@/src-refactor/modules/store/qrtk/services/realestate/responses/property-details.response";

/**
 * preparePropertyDetailToCard
 *
 * Convierte los datos de detalle de propiedad al formato ICardProperty
 * necesario para funcionalidades como referir propiedad.
 * Reutiliza la lógica de list-property-adapter para mantener consistencia.
 */
export const preparePropertyDetailToCard = (
  detailData: any, // Using 'any' here to break circular dependency - type is inferred from propertyDetailAdapter
  rawData: IPropertyDetailsResponse
): import("@/src-refactor/modules/shared/components/refactor/model/card-property.model").ICardProperty => {
  const {
    prepareFeature
  } = require("@/src-refactor/modules/shared/components/refactor/helpers/property/prepare-feature");
  const {
    HPreparePrice
  } = require("@/src-refactor/modules/shared/components/refactor/helpers/property/prepare-price-property");
  const {
    O_BUSINESS_TYPE
  } = require("@/src-refactor/modules/shared/model/const/property/business-type.const");

  // Preparar features usando el mismo helper que list-property-adapter
  const feature = prepareFeature({
    bathrooms: detailData.features?.bathrooms ?? null,
    halfBathrooms: detailData.features?.halfBathrooms ?? null,
    bedrooms: detailData.features?.bedrooms ?? null,
    floorLevel: detailData.features?.floorLevel ?? null,
    floorsTotal: detailData.features?.floorsTotal ?? null,
    sqmLand: detailData.features?.sqmLand ?? null,
    sqmConstruction: detailData.features?.sqmConstruction ?? null,
    pumps: detailData.features?.pumps ?? null,
    parkingSpots: detailData.features?.parkingSpots ?? null
  });

  // Preparar precio usando los datos raw (ya que price es un array en detailData)
  const budget = HPreparePrice({
    alternativePrice: rawData?.alternate_price?.toString() ?? "",
    currencyAlternativePrice: rawData?.alternative_currency_symbol ?? "",
    currencyPrice: rawData?.currency_symbol ?? "",
    businessTypeId: detailData.businessType
      ? O_BUSINESS_TYPE[detailData.businessType]?.id
      : 0,
    isProject: Boolean(detailData.isProject),
    price: rawData?.price ?? 0,
    maximumPrice: rawData?.project?.maximum_price?.toString() ?? "",
    minimumPrice: rawData?.project?.minimum_price?.toString() ?? ""
  });

  // Preparar location
  const location = `${detailData.sector ?? ""}, ${detailData.city ?? ""}`;

  // Preparar agentes (usando datos raw para obtener agency_id)
  const agentsInfoFull =
    rawData?.agents?.map(agent => ({
      name: agent.name,
      id: Number(agent.id),
      email: agent.email,
      agencyId: agent.agency_id ?? 0,
      picture: agent.picture ?? null
    })) ?? [];

  const agentsNames = agentsInfoFull.map(agent => agent.name).join(", ");

  // Preparar permisos
  const permissionsProps = {
    agents: agentsInfoFull.map(agent => ({
      id: agent.id,
      agencyId: agent.agencyId
    })),
    isProject: Boolean(detailData.isProject),
    idStatusProperty: detailData.status?.id ?? 0
  };

  return {
    id: detailData.id,
    title: detailData.title ?? "",
    conditionStatus: detailData.condition ?? undefined,
    businessType: detailData.businessType ?? "",
    pictures: detailData.images ?? [],
    prices: budget,
    feature: feature,
    views: 0, // No disponible en detalle
    likes: 0, // No disponible en detalle
    agents: agentsNames,
    agentsInfoFull: agentsInfoFull,
    status: detailData.status?.name ?? "",
    commissionGaranted: detailData.commission?.total ?? 0,
    nameProperty: detailData.name ?? "",
    location: location,
    syncStatus: undefined, // No disponible en detalle
    isCollection: Boolean(detailData.isCollection),
    lat: detailData.latitude ?? 0,
    lng: detailData.longitude ?? 0,
    permissionsProps: permissionsProps,
    dirtyState: rawData as any // Usar datos raw como dirtyState
  };
};
